import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { useAuth } from "../../context/AuthContext";
import type { Deal, TeamMember } from "../../lib/types";
import DealLink from "../DealLink";
import DispatchQuestionPill from "./comms/DispatchQuestionPill";
import SchedulePreModal from "./actions/SchedulePreModal";
import ScheduleAdjusterModal from "./actions/ScheduleAdjusterModal";
import ScheduleBuildModal from "./actions/ScheduleBuildModal";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { notifyChadOnBuildApproval } from "../../lib/notifyChadOnBuildApproval";
import { passToNotifyPlan, useBuildSubmitGate } from "../deal/BuildPaymentPlanModal";
import { notifyAdjusterReady } from "../../lib/notifyAdjusterReady";
import EditableCell from "./RoofMasterCell";
import RoofMasterColumnManager from "./RoofMasterColumnManager";
import {
  ROOF_MASTER_SELECT,
  GATED_VIEW_ROLES,
  applyLayout,
  orderedColumns,
  customFieldsToColumns,
  utcIsoToLocalInput,
  localInputToUtcIso,
  type ColumnDef,
  type LayoutEntry,
  type CustomFieldDef,
} from "./roofMasterColumns";

/* Roles allowed to edit the shared column layout + views — mirrors the
   rml_write / rmv_write RLS policies (get_my_role() returns 'admin' for supers). */
const LAYOUT_EDIT_ROLES = new Set<string>([
  "admin", "sales_director", "asm", "roof_team_manager", "roof_director", "director_of_operations",
]);

/* Ship 4 — saved views (bottom tabs). */
interface ViewFilters { status?: string; owner?: string; rs?: string; search?: string }
interface RoofView { id: string; name: string; sort_order: number; columns: LayoutEntry[]; filters: ViewFilters }

/**
 * Roof Master — registry-driven editable grid (Ship 1).
 *
 * Robert's spreadsheet view of all in-flight deals. Lives below the Schedule
 * tab on the dispatch board. Desktop-only.
 *
 * Columns are now defined in `roofMasterColumns.ts` (the registry). Most cells
 * are inline-editable via <EditableCell> (click a cell → type → blur/Enter
 * saves straight to `deals`). A handful stay BESPOKE because they carry
 * business logic beyond a plain field write:
 *   • homeowner_name  → DealLink
 *   • _status         → status dropdown that runs a stage transition (+ Chad
 *                       notify on build_submitted)
 *   • adjuster_meeting_date → datetime with confirm-on-clear + overdue styling
 *   • assigned_rs_id  → RS assign (mirrors to pre_inspection_assigned_to)
 *   • _address        → maps link
 *   • _owner / _hasPics / _hasHover → computed display
 * Actions column (💬 Ask · ✓ PRE · 🗓️ Schedule) is unchanged.
 *
 * Gated columns (crew pay / material cost) are hidden from non-managers.
 */

/* ────────────────────────────────────────────────────────────────
   STATUS MAPPING — Robert's labels ↔ DB stages
   ──────────────────────────────────────────────────────────────── */
type RoofMasterStatus = "Adj Meet" | "Pre Inspection" | "Reinspect" | "Waiting on Claims" | "Build";

const STATUS_LABEL: Record<RoofMasterStatus, string> = {
  "Adj Meet": "Adj Meet",
  "Pre Inspection": "Pre Inspection",
  "Reinspect": "Reinspect",
  "Waiting on Claims": "Waiting on Claims",
  "Build": "Build",
};

function dealStatus(d: Deal): RoofMasterStatus | null {
  switch (d.stage) {
    case "adjuster_meeting_ready":
    case "adjuster_meeting_scheduled":
    case "pre_inspection_complete":
      return "Adj Meet";
    case "assign_roof_specialist":
    case "pre_inspection_ready":
      return "Pre Inspection";
    case "partial_approval":
    case "docs_and_contingency":
    case "reinspection_ready":
    case "reinspection_filed":
      return "Reinspect";
    case "waiting_on_claims":
      return "Waiting on Claims";
    case "schedule_build":
    case "build_scheduled":
    case "build_submitted":
    case "install_meeting":
      return "Build";
    default:
      return null;
  }
}

/* Stage to flip to when status dropdown changes.
   "Build" → build_submitted (Chad's approval queue), NOT schedule_build —
   routing through build_submitted keeps Chad's scope review in the loop. */
const STATUS_TO_STAGE: Record<RoofMasterStatus, string> = {
  "Adj Meet": "adjuster_meeting_ready",
  "Pre Inspection": "pre_inspection_ready",
  "Reinspect": "reinspection_ready",
  "Waiting on Claims": "waiting_on_claims",
  "Build": "build_submitted",
};

/* Roles eligible to be assigned as the Roof Specialist. */
const RS_ROLES = new Set([
  "roof_specialist", "roof_team_manager", "qc", "roof_director",
  "director_of_operations", "team_lead", "asm", "restore_manager",
]);

/* Status badge color classes */
const STATUS_BADGE: Record<RoofMasterStatus, string> = {
  "Adj Meet": "bg-orange-100 text-orange-700 border-orange-200",
  "Pre Inspection": "bg-amber-100 text-amber-800 border-amber-200",
  "Reinspect": "bg-pink-100 text-pink-700 border-pink-200",
  "Waiting on Claims": "bg-blue-100 text-blue-700 border-blue-200",
  "Build": "bg-emerald-100 text-emerald-700 border-emerald-200",
};

interface RoofMasterDeal extends Deal {
  _status: RoofMasterStatus;
  _ownerName: string;
  _splitName: string;
  _rsName: string;
  _hasPics: boolean;
  _hasHover: boolean;
  _isOverdue: boolean;
}

/* The deal field a column sorts by (computed columns map to their backing field). */
function sortFieldFor(key: string): string {
  switch (key) {
    case "_address": return "property_street";
    case "_owner": return "_ownerName";
    case "assigned_rs_id": return "_rsName";
    default: return key;
  }
}

/* ────────────────────────────────────────────────────────────────
   COMPONENT
   ──────────────────────────────────────────────────────────────── */
interface RoofMasterTableProps {
  /** Bumped by parent on realtime change. Drives a refetch. */
  reloadKey?: number;
}

export default function RoofMasterTable({ reloadKey = 0 }: RoofMasterTableProps) {
  const { role, isSuperUser, member } = useAuth();
  // "Build" from an earlier status submits the build (Oct 21, 2026): ask how the
  // first payment will be collected when it isn't captured.
  const gate = useBuildSubmitGate();
  const [deals, setDeals] = useState<RoofMasterDeal[] | null>(null);
  const [members, setMembers] = useState<Record<string, TeamMember>>({});
  const [err, setErr] = useState<string | null>(null);
  const [localKey, setLocalKey] = useState(0);
  const reload = () => setLocalKey((k) => k + 1);

  useRealtimeTable("deals", reload);

  // Filters
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | RoofMasterStatus>("all");
  const [filterOwner, setFilterOwner] = useState<string>("all");
  const [filterRs, setFilterRs] = useState<string>("all");

  // Sort
  const [sortKey, setSortKey] = useState<string>("_status");
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  // Selection (bulk)
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Action modals
  const [schedFor, setSchedFor] = useState<{ deal: RoofMasterDeal; type: RoofMasterStatus } | null>(null);
  const [confirmStatusChange, setConfirmStatusChange] = useState<{ deal: RoofMasterDeal; toStatus: RoofMasterStatus } | null>(null);
  const [confirmClearDate, setConfirmClearDate] = useState<RoofMasterDeal | null>(null);
  const [highlightAddFor, setHighlightAddFor] = useState<string | null>(null);

  /* Gated (crew $ / cost) columns visible only to managers / super users. */
  const canSeeGated = isSuperUser || (!!role && GATED_VIEW_ROLES.has(role));
  const canManageLayout = isSuperUser || (!!role && LAYOUT_EDIT_ROLES.has(role));

  /* Shared layout (Ship 2) + custom fields (Ship 3) + saved views (Ship 4). */
  const [layout, setLayout] = useState<LayoutEntry[] | null>(null);
  const [customDefs, setCustomDefs] = useState<CustomFieldDef[]>([]);
  const [views, setViews] = useState<RoofView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null); // null = "All"
  const [structKey, setStructKey] = useState(0);
  const [showColMgr, setShowColMgr] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  useRealtimeTable("roof_master_layout", () => setStructKey((k) => k + 1));
  useRealtimeTable("custom_field_definitions", () => setStructKey((k) => k + 1));
  useRealtimeTable("roof_master_views", () => setStructKey((k) => k + 1));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: lay }, { data: defs }, { data: vs }] = await Promise.all([
        supabase.from("roof_master_layout").select("columns").eq("id", 1).maybeSingle(),
        supabase.from("custom_field_definitions").select("id,key,label,field_type,options,sort_order").eq("is_active", true).order("sort_order"),
        supabase.from("roof_master_views").select("id,name,sort_order,columns,filters").order("sort_order"),
      ]);
      if (cancelled) return;
      setLayout((lay?.columns as LayoutEntry[] | null) ?? null);
      setCustomDefs((defs as CustomFieldDef[] | null) ?? []);
      setViews((vs as RoofView[] | null) ?? []);
    })();
    return () => { cancelled = true; };
  }, [structKey]);

  const activeView = activeViewId ? views.find((v) => v.id === activeViewId) ?? null : null;
  // Columns come from the active view, or the global "All" layout.
  const effectiveLayout = activeView ? activeView.columns : layout;

  const customCols = useMemo<ColumnDef[]>(() => customFieldsToColumns(customDefs), [customDefs]);
  const visibleColumns = useMemo<ColumnDef[]>(
    () => applyLayout(effectiveLayout, canSeeGated, customCols),
    [effectiveLayout, canSeeGated, customCols],
  );

  /* Switch tabs — applies the view's saved filters (All clears them). */
  const selectView = (id: string | null) => {
    setActiveViewId(id);
    const f = (id ? views.find((v) => v.id === id)?.filters : null) ?? {};
    setFilterStatus((f.status as "all" | RoofMasterStatus) ?? "all");
    setFilterOwner(f.owner ?? "all");
    setFilterRs(f.rs ?? "all");
    setSearch(f.search ?? "");
  };

  /* Serialize the current ordered columns (incl. hidden + overrides) for saving. */
  const snapshotColumns = (): LayoutEntry[] =>
    orderedColumns(effectiveLayout, customCols).map((o) => ({
      key: o.col.key, hidden: o.hidden || undefined, w: o.col.width, label: o.col.label,
    }));
  const currentFilters = (): ViewFilters => ({ status: filterStatus, owner: filterOwner, rs: filterRs, search });

  /* ⚙ Columns Save → writes to the active view, or the global layout for "All". */
  const persistColumns = async (entries: LayoutEntry[]) => {
    setSavingLayout(true);
    setErr(null);
    try {
      const error = activeViewId
        ? (await supabase.from("roof_master_views").update({ columns: entries, updated_by: member?.id ?? null, updated_at: new Date().toISOString() }).eq("id", activeViewId)).error
        : (await supabase.from("roof_master_layout").upsert({ id: 1, columns: entries, updated_by: member?.id ?? null, updated_at: new Date().toISOString() })).error;
      if (error) throw error;
      setShowColMgr(false);
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingLayout(false);
    }
  };
  /* ⚙ Reset → clears the active view's columns (back to default order), or
     deletes the global layout row for "All". */
  const resetColumns = async () => {
    setSavingLayout(true);
    setErr(null);
    try {
      const error = activeViewId
        ? (await supabase.from("roof_master_views").update({ columns: [], updated_by: member?.id ?? null, updated_at: new Date().toISOString() }).eq("id", activeViewId)).error
        : (await supabase.from("roof_master_layout").delete().eq("id", 1)).error;
      if (error) throw error;
      setShowColMgr(false);
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingLayout(false);
    }
  };

  /* New view — snapshots current columns + filters, then selects it. */
  const createView = async () => {
    const name = window.prompt("Name this view (e.g. Adjuster Meetings):")?.trim();
    if (!name) return;
    setErr(null);
    try {
      const { data, error } = await supabase.from("roof_master_views").insert({
        name, columns: snapshotColumns(), filters: currentFilters(),
        sort_order: views.length, created_by: member?.id ?? null, updated_by: member?.id ?? null,
      }).select("id,name,sort_order,columns,filters").single();
      if (error) throw error;
      if (data) {
        // Optimistically add so the new tab appears + activates without a gap
        // before the realtime refetch reconciles.
        setViews((prev) => [...prev, data as RoofView]);
        setActiveViewId((data as RoofView).id);
      }
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  /* Update the active view to the current columns + filters. */
  const updateView = async () => {
    if (!activeViewId) return;
    setErr(null);
    try {
      const { error } = await supabase.from("roof_master_views").update({
        columns: snapshotColumns(), filters: currentFilters(), updated_by: member?.id ?? null, updated_at: new Date().toISOString(),
      }).eq("id", activeViewId);
      if (error) throw error;
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const renameView = async () => {
    if (!activeView) return;
    const name = window.prompt("Rename view:", activeView.name)?.trim();
    if (!name) return;
    setErr(null);
    try {
      const { error } = await supabase.from("roof_master_views").update({ name, updated_by: member?.id ?? null }).eq("id", activeView.id);
      if (error) throw error;
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const deleteView = async () => {
    if (!activeView) return;
    if (!window.confirm(`Delete the "${activeView.name}" view for everyone?`)) return;
    setErr(null);
    try {
      const { error } = await supabase.from("roof_master_views").delete().eq("id", activeView.id);
      if (error) throw error;
      setActiveViewId(null);
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  /* Create a custom field (manager keeps the drawer open). Slug derived from label. */
  const createCustomField = async (label: string, fieldType: CustomFieldDef["field_type"], options: { value: string; label: string }[]) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || `field_${Date.now()}`;
    setErr(null);
    try {
      const { error } = await supabase.from("custom_field_definitions").insert({
        key, label: trimmed, field_type: fieldType,
        options: fieldType === "select" ? options : [],
        sort_order: customDefs.length, created_by: member?.id ?? null,
      });
      if (error) throw error;
      setStructKey((k) => k + 1);
    } catch (e) {
      // Most likely a duplicate key (unique constraint) — surface it.
      setErr(`Couldn't add field: ${(e as Error).message}`);
    }
  };

  /* Soft-delete a custom field (values stay in deals.custom_fields). */
  const deleteCustomField = async (cfKey: string) => {
    setErr(null);
    try {
      const { error } = await supabase.from("custom_field_definitions").update({ is_active: false }).eq("key", cfKey);
      if (error) throw error;
      setStructKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  /* Load — pulls in-flight deals across PRE/Adj/Reinspect/Build */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const masterStages = [
          "assign_roof_specialist", "pre_inspection_ready", "pre_inspection_complete",
          "adjuster_meeting_ready", "adjuster_meeting_scheduled",
          "waiting_on_claims",
          "partial_approval", "docs_and_contingency", "reinspection_ready", "reinspection_filed",
          "schedule_build", "build_scheduled", "build_submitted", "install_meeting",
        ];

        const [{ data, error }, { data: roster }] = await Promise.all([
          supabase.from("deals").select(ROOF_MASTER_SELECT).in("stage", masterStages),
          supabase.from("team_roster").select("id,full_name,role,is_active").eq("is_active", true),
        ]);
        if (error) throw error;

        const memberMap: Record<string, TeamMember> = {};
        (roster ?? []).forEach((m: { id: string } & Record<string, unknown>) => { memberMap[m.id] = m as unknown as TeamMember; });

        const today = new Date();
        const dealRows = (data ?? []) as unknown as Deal[];
        const enriched: RoofMasterDeal[] = dealRows.map((d: Deal) => {
          const status = dealStatus(d);
          const ownerName = d.owner_id ? memberMap[d.owner_id]?.full_name ?? "—" : "—";
          const splitName = d.split_rm_id && d.split_rm_id !== d.owner_id
            ? memberMap[d.split_rm_id]?.full_name ?? "" : "";
          const rsName = d.assigned_rs_id ? memberMap[d.assigned_rs_id]?.full_name ?? "" : "";
          const hasPics = !!d.imging_report_link;
          const hasHover = !!d.hover_link;
          const adj = d.adjuster_meeting_date ? new Date(d.adjuster_meeting_date) : null;
          const isOverdue = !!(adj && adj < today && status === "Adj Meet");
          return {
            ...d,
            _status: (status ?? "Waiting on Claims") as RoofMasterStatus,
            _ownerName: ownerName,
            _splitName: splitName,
            _rsName: rsName,
            _hasPics: hasPics,
            _hasHover: hasHover,
            _isOverdue: isOverdue,
          };
        }).filter((d) => d._status);

        if (!cancelled) {
          setDeals(enriched);
          setMembers(memberMap);
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey, localKey]);

  /* Derived: filter + sort */
  const filtered = useMemo(() => {
    if (!deals) return [];
    let rows = deals;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((d) =>
        (d.homeowner_name ?? "").toLowerCase().includes(q) ||
        (d.property_street ?? "").toLowerCase().includes(q) ||
        (d.property_city ?? "").toLowerCase().includes(q) ||
        (d.insurance_company ?? "").toLowerCase().includes(q) ||
        (d.claim_number ?? "").toLowerCase().includes(q),
      );
    }
    if (filterStatus !== "all") rows = rows.filter((d) => d._status === filterStatus);
    if (filterOwner !== "all") rows = rows.filter((d) => d.owner_id === filterOwner);
    if (filterRs !== "all") rows = rows.filter((d) => d.assigned_rs_id === filterRs);

    rows = [...rows].sort((a, b) => {
      const av = String((a as unknown as Record<string, unknown>)[sortKey] ?? "").toLowerCase();
      const bv = String((b as unknown as Record<string, unknown>)[sortKey] ?? "").toLowerCase();
      return sortDir * av.localeCompare(bv);
    });
    return rows;
  }, [deals, search, filterStatus, filterOwner, filterRs, sortKey, sortDir]);

  const ownerOptions = useMemo(() => {
    const ids = new Set<string>();
    (deals ?? []).forEach((d) => d.owner_id && ids.add(d.owner_id));
    return Array.from(ids).map((id) => members[id]).filter(Boolean).sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [deals, members]);

  const rsOptions = useMemo(() => {
    const ids = new Set<string>();
    (deals ?? []).forEach((d) => d.assigned_rs_id && ids.add(d.assigned_rs_id));
    return Array.from(ids).map((id) => members[id]).filter(Boolean).sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [deals, members]);

  const rsCandidates = useMemo(
    () => Object.values(members).filter((m) => m.is_active && RS_ROLES.has(m.role)).sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [members],
  );

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortKey(key); setSortDir(1); }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  /* Inline status change */
  const changeStatus = async (deal: RoofMasterDeal, toStatus: RoofMasterStatus) => {
    if (deal._status === toStatus) return;
    setConfirmStatusChange({ deal, toStatus });
  };

  const confirmAndApply = async () => {
    if (!confirmStatusChange) return;
    const { deal, toStatus } = confirmStatusChange;
    const newStage = STATUS_TO_STAGE[toStatus];
    setConfirmStatusChange(null);
    try {
      const pass = await gate.run(
        { dealId: deal.id, homeowner: deal.homeowner_name, fromStage: deal.stage, toStage: newStage, source: "roof_master", title: "Submit build" },
        async () => {
          const { error } = await supabase.from("deals").update({ stage: newStage }).eq("id", deal.id);
          if (error) throw error;
        },
      );
      if (!pass) return; // cancelled
      if (newStage === "build_submitted" && deal.stage !== "build_submitted") {
        void notifyChadOnBuildApproval(deal, { actorId: member?.id, plan: passToNotifyPlan(pass) });
      }
      reload();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  /* Inline RS assignment — writes assigned_rs_id, mirrors to
     pre_inspection_assigned_to, stamps rs_assignment_date. No stage change. */
  const [savingRsFor, setSavingRsFor] = useState<string | null>(null);

  const assignRs = async (deal: RoofMasterDeal, rsId: string) => {
    const newId = rsId || null;
    if ((deal.assigned_rs_id ?? null) === newId) return;
    setSavingRsFor(deal.id);
    setErr(null);
    try {
      const patch: Record<string, unknown> = { assigned_rs_id: newId, pre_inspection_assigned_to: newId };
      if (newId) patch.rs_assignment_date = new Date().toISOString();
      const { error } = await supabase.from("deals").update(patch).eq("id", deal.id);
      if (error) throw error;
      reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingRsFor(null);
    }
  };

  const openSchedule = (deal: RoofMasterDeal) => setSchedFor({ deal, type: deal._status });

  /* Mark PRE done → advance pre_inspection_ready to adjuster_meeting_ready. */
  const [preDoneFor, setPreDoneFor] = useState<string | null>(null);

  const markPreDone = async (deal: RoofMasterDeal) => {
    if (deal.stage !== "pre_inspection_ready") return;
    setPreDoneFor(deal.id);
    setErr(null);
    try {
      const { error } = await supabase
        .from("deals")
        .update({
          pre_inspection_completed: true,
          pre_inspection_completed_date: new Date().toISOString(),
          stage: "adjuster_meeting_ready",
        })
        .eq("id", deal.id);
      if (error) throw error;
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        note_type: "stage_change",
        content: "Pre-inspection marked complete from Roof Master.",
        metadata: { from_stage: "pre_inspection_ready", to_stage: "adjuster_meeting_ready" },
      });
      void notifyAdjusterReady(deal);
      reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setPreDoneFor(null);
    }
  };

  /* Inline adjuster_meeting_date edit (special — confirm on clear). */
  const [savingDateFor, setSavingDateFor] = useState<string | null>(null);

  const persistAdjusterDate = async (dealId: string, iso: string | null) => {
    setSavingDateFor(dealId);
    setErr(null);
    try {
      const { error } = await supabase.from("deals").update({ adjuster_meeting_date: iso }).eq("id", dealId);
      if (error) throw error;
      reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingDateFor(null);
    }
  };

  const onAdjusterDateChange = (deal: RoofMasterDeal, inputValue: string) => {
    if (!inputValue) { setConfirmClearDate(deal); return; }
    setHighlightAddFor(null);
    persistAdjusterDate(deal.id, localInputToUtcIso(inputValue));
  };

  const confirmClearAndApply = async () => {
    if (!confirmClearDate) return;
    const dealId = confirmClearDate.id;
    setConfirmClearDate(null);
    await persistAdjusterDate(dealId, null);
  };

  const cancelClear = () => {
    if (!confirmClearDate) return;
    setHighlightAddFor(confirmClearDate.id);
    setConfirmClearDate(null);
    reload();
  };

  /* Render one of the bespoke (non-EditableCell) cells. */
  function renderSpecialCell(col: ColumnDef, d: RoofMasterDeal) {
    switch (col.key) {
      case "homeowner_name":
        return (
          <span className="font-semibold text-bardin-ink">
            <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
          </span>
        );
      case "_hasPics":
        return <YN v={d._hasPics} />;
      case "_hasHover":
        return <YN v={d._hasHover} />;
      case "_status":
        return (
          <select
            value={d._status}
            onChange={(e) => changeStatus(d, e.target.value as RoofMasterStatus)}
            className={"text-[10px] font-semibold px-1.5 py-0.5 rounded border cursor-pointer " + STATUS_BADGE[d._status]}
          >
            {(Object.keys(STATUS_LABEL) as RoofMasterStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        );
      case "_address":
        return isAddressLinkable(d) ? (
          <a
            href={mapsHref(d)}
            target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-slate-600 hover:text-bardin-orange truncate block"
            title={[d.property_street, d.property_city].filter(Boolean).join(", ")}
          >
            {d.property_street}{d.property_city ? `, ${d.property_city}` : ""}
          </a>
        ) : (
          <span className="text-slate-600 truncate block" title={[d.property_street, d.property_city].filter(Boolean).join(", ")}>
            {d.property_street}{d.property_city ? `, ${d.property_city}` : ""}
          </span>
        );
      case "_owner":
        return (
          <span>{d._ownerName}{d._splitName && <span className="text-slate-400"> + {d._splitName.split(" ")[0]}</span>}</span>
        );
      case "adjuster_meeting_date":
        return (
          <div className="whitespace-nowrap">
            <input
              type="datetime-local"
              value={utcIsoToLocalInput(d.adjuster_meeting_date)}
              onChange={(e) => onAdjusterDateChange(d, e.target.value)}
              onFocus={() => { if (highlightAddFor === d.id) setHighlightAddFor(null); }}
              disabled={savingDateFor === d.id}
              className={
                "border rounded px-1 py-0.5 text-[11px] " +
                (highlightAddFor === d.id
                  ? "border-amber-500 ring-2 ring-amber-300 bg-amber-50 font-semibold animate-pulse"
                  : d._isOverdue
                  ? "border-red-300 text-red-700 font-semibold bg-red-50/50"
                  : "border-slate-200") +
                (savingDateFor === d.id ? " opacity-50" : "")
              }
              title={highlightAddFor === d.id ? "Pick a date and time for this meeting" : "Click to set or change adjuster meeting"}
            />
            {d._isOverdue && <span className="ml-1 text-red-600">⚠</span>}
            {savingDateFor === d.id && <span className="ml-1 text-[10px] text-slate-400">saving…</span>}
          </div>
        );
      case "assigned_rs_id": {
        const cur = d.assigned_rs_id ?? "";
        const curInList = !cur || rsCandidates.some((m) => m.id === cur);
        return (
          <div>
            <select
              value={cur}
              onChange={(e) => assignRs(d, e.target.value)}
              disabled={savingRsFor === d.id}
              className={
                "border rounded px-1 py-0.5 text-[11px] bg-white max-w-[140px] cursor-pointer " +
                (cur ? "border-slate-200 text-slate-700" : "border-slate-200 text-slate-400") +
                (savingRsFor === d.id ? " opacity-50" : "")
              }
              title="Assign or change the roof specialist"
            >
              <option value="">— Unassigned</option>
              {!curInList && cur && (<option value={cur}>{d._rsName || members[cur]?.full_name || "Current assignee"}</option>)}
              {rsCandidates.map((m) => (<option key={m.id} value={m.id}>{m.full_name}</option>))}
            </select>
            {savingRsFor === d.id && <span className="ml-1 text-[10px] text-slate-400">saving…</span>}
          </div>
        );
      }
      default:
        return null;
    }
  }

  /* Empty / loading / error */
  if (err) return <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">Roof Master — {err}</div>;
  if (!deals) return <div className="text-slate-400 text-sm py-3">Loading Roof Master…</div>;

  const totalCols = 1 + visibleColumns.length + 1; // checkbox + columns + actions

  /* ─────── render ─────── */
  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / address / insurance / claim #…"
          className="border border-slate-300 rounded-md px-3 py-1.5 text-sm min-w-[280px]"
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white">
          <option value="all">All statuses</option>
          {(Object.keys(STATUS_LABEL) as RoofMasterStatus[]).map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white">
          <option value="all">All owners</option>
          {ownerOptions.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
        </select>
        <select value={filterRs} onChange={(e) => setFilterRs(e.target.value)} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white">
          <option value="all">All RS</option>
          {rsOptions.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
        </select>
        {canManageLayout && (
          <button
            onClick={() => setShowColMgr(true)}
            className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white hover:bg-slate-50"
            title="Customize columns (shared layout)"
          >
            ⚙ Columns
          </button>
        )}
        <span className="text-xs text-slate-500 ml-auto">
          {filtered.length} of {deals.length} {selected.size > 0 ? `· ${selected.size} selected` : ""}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-left w-8"></th>
                {visibleColumns.map((col) => (
                  <Th
                    key={col.key}
                    label={col.label}
                    k={sortFieldFor(col.key)}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  />
                ))}
                <th className="px-2 py-2 text-left whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={totalCols} className="text-center py-6 text-slate-400 text-xs">No matches</td></tr>
              ) : (
                filtered.map((d) => {
                  const isSel = selected.has(d.id);
                  const rowCls = isSel ? "bg-orange-50" : d._isOverdue ? "bg-amber-50/50" : "hover:bg-slate-50";
                  return (
                    <tr key={d.id} className={rowCls + " border-b border-slate-100"}>
                      <td className="px-2 py-1.5">
                        <input type="checkbox" checked={isSel} onChange={() => toggleSelect(d.id)} className="cursor-pointer" />
                      </td>
                      {visibleColumns.map((col) => (
                        <td
                          key={col.key}
                          className="px-2 py-1.5 align-top"
                          style={
                            col.width
                              ? col.type === "special"
                                // Special cells hold interactive widgets (datetime input, RS
                                // select) — only set a floor; capping width clips them.
                                ? { minWidth: col.width }
                                : { minWidth: col.width, maxWidth: col.width }
                              : undefined
                          }
                        >
                          {col.type === "special"
                            ? renderSpecialCell(col, d)
                            : (
                              <EditableCell
                                dealId={d.id}
                                column={col}
                                value={
                                  col.custom
                                    ? ((d as unknown as { custom_fields?: Record<string, unknown> }).custom_fields ?? {})[col.cfKey as string]
                                    : (d as unknown as Record<string, unknown>)[col.key]
                                }
                                onSaved={reload}
                              />
                            )}
                        </td>
                      ))}
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <DispatchQuestionPill
                            deal={{
                              id: d.id, homeowner_name: d.homeowner_name, property_street: d.property_street,
                              owner_id: d.owner_id, split_rm_id: d.split_rm_id ?? null,
                              pre_inspection_assigned_to: d.assigned_rs_id ?? d.pre_inspection_assigned_to ?? null, phase: d.phase ?? null,
                            }}
                          />
                          {d.stage === "pre_inspection_ready" && (
                            <button
                              onClick={() => { if (preDoneFor !== d.id) void markPreDone(d); }}
                              disabled={preDoneFor === d.id}
                              className={
                                "px-1.5 py-1 text-[11px] border rounded transition " +
                                (preDoneFor === d.id
                                  ? "border-slate-200 text-slate-300 cursor-not-allowed"
                                  : "border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600")
                              }
                              title="Mark pre-inspection complete → move to Adjuster Meeting Ready"
                            >
                              {preDoneFor === d.id ? "…" : "✓ PRE"}
                            </button>
                          )}
                          {(() => {
                            const isBuild = d._status === "Build";
                            const buildDisabled = isBuild && d.stage !== "schedule_build";
                            const buildTitle = (() => {
                              if (!isBuild) {
                                return `Schedule ${d._status === "Adj Meet" ? "adjuster meeting" : d._status === "Pre Inspection" ? "PRE" : "next step"}`;
                              }
                              switch (d.stage) {
                                case "install_meeting": return "Waiting on the RM to submit for Chad's approval";
                                case "build_submitted": return "Waiting on build approval (Chad or Robert, Build Approvals board) — once approved, you can schedule";
                                case "build_scheduled": return "Already on the calendar — open the deal to reschedule";
                                case "schedule_build": return "Schedule build";
                                default: return "Schedule build";
                              }
                            })();
                            return (
                              <button
                                onClick={() => { if (!buildDisabled) openSchedule(d); }}
                                disabled={buildDisabled}
                                className={
                                  "px-1.5 py-1 text-[11px] border rounded transition " +
                                  (buildDisabled
                                    ? "border-slate-200 text-slate-300 cursor-not-allowed"
                                    : "border-slate-200 hover:bg-bardin-orange hover:text-white hover:border-bardin-orange")
                                }
                                title={buildTitle}
                              >
                                🗓️
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="bg-bardin-ink text-white px-4 py-2.5 flex items-center gap-3 text-xs">
            <span><strong>{selected.size}</strong> rows selected</span>
            <button onClick={() => alert("Bulk Schedule Adjuster — coming next ship")} className="px-3 py-1 rounded bg-bardin-orange font-semibold hover:opacity-90">📅 Bulk Schedule Adjuster</button>
            <button onClick={() => alert("Bulk Ask RM — coming next ship")} className="px-3 py-1 rounded bg-bardin-orange font-semibold hover:opacity-90">💬 Ask RMs</button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1 rounded border border-white/30 hover:bg-white/10 ml-auto">Clear</button>
          </div>
        )}
      </div>

      {/* Saved-view tabs (Ship 4) */}
      <div className="flex items-center gap-1 mt-2 overflow-x-auto pb-1">
        <ViewTab label="All" active={activeViewId === null} onClick={() => selectView(null)} />
        {views.map((v) => (
          <ViewTab key={v.id} label={v.name} active={activeViewId === v.id} onClick={() => selectView(v.id)} />
        ))}
        {canManageLayout && (
          <button onClick={createView} title="New view from the current columns + filters" className="px-2 py-1 text-sm text-bardin-orange font-semibold hover:bg-orange-50 rounded shrink-0">＋ View</button>
        )}
        {canManageLayout && activeViewId && (
          <span className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200 shrink-0">
            <button onClick={updateView} className="px-2 py-1 text-[11px] border border-slate-200 rounded hover:bg-slate-50" title="Save current columns + filters to this tab">⤓ Save tab</button>
            <button onClick={renameView} className="px-1.5 py-1 text-[11px] border border-slate-200 rounded hover:bg-slate-50" title="Rename view">✎</button>
            <button onClick={deleteView} className="px-1.5 py-1 text-[11px] border border-slate-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200" title="Delete view">🗑</button>
          </span>
        )}
      </div>

      {/* Confirm clearing the adjuster meeting date */}
      {confirmClearDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={cancelClear}>
          <div className="bg-white rounded-lg shadow-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-bardin-ink mb-2">Remove from calendar?</h3>
            <p className="text-sm text-slate-600 mb-2">Clear the adjuster meeting for <span className="font-semibold">{confirmClearDate.homeowner_name}</span>?</p>
            <p className="text-xs text-slate-500 mb-4">The deal stays at Adjuster Meeting Ready, but it won't appear on the schedule until a new date is set.</p>
            <div className="flex justify-end gap-2">
              <button onClick={cancelClear} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800">No, keep the date</button>
              <button onClick={confirmClearAndApply} className="px-4 py-1.5 rounded bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Yes, remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm status change */}
      {gate.element}
      {confirmStatusChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmStatusChange(null)}>
          <div className="bg-white rounded-lg shadow-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-bardin-ink mb-2">Change status?</h3>
            <p className="text-sm text-slate-600 mb-4">
              Move <span className="font-semibold">{confirmStatusChange.deal.homeowner_name}</span> from{" "}
              <span className="font-semibold">{confirmStatusChange.deal._status}</span> to{" "}
              <span className="font-semibold">{confirmStatusChange.toStatus}</span>?
            </p>
            <p className="text-xs text-slate-500 mb-4">Stage will flip to <code className="bg-slate-100 px-1 rounded">{STATUS_TO_STAGE[confirmStatusChange.toStatus]}</code>.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmStatusChange(null)} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
              <button onClick={confirmAndApply} className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-semibold">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Context-aware schedule modal */}
      {schedFor?.type === "Pre Inspection" && (
        <SchedulePreModal open deal={schedFor.deal} onClose={() => setSchedFor(null)} onCompleted={() => { setSchedFor(null); reload(); }} />
      )}
      {schedFor?.type === "Adj Meet" && (
        <ScheduleAdjusterModal open deal={schedFor.deal} onClose={() => setSchedFor(null)} onCompleted={() => { setSchedFor(null); reload(); }} />
      )}
      {schedFor?.type === "Build" && (
        <ScheduleBuildModal open deal={schedFor.deal} onClose={() => setSchedFor(null)} onCompleted={() => { setSchedFor(null); reload(); }} />
      )}
      {schedFor && (schedFor.type === "Reinspect" || schedFor.type === "Waiting on Claims") && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSchedFor(null)}>
          <div className="bg-white rounded-lg shadow-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm text-slate-600">No scheduling action available for {schedFor.type}. Open the deal directly to manage.</p>
            <div className="flex justify-end mt-3">
              <button onClick={() => setSchedFor(null)} className="px-3 py-1.5 text-sm text-slate-600">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Column manager (Ship 2 + 3) — edits the active view's columns (or the
          global "All" layout). */}
      {showColMgr && (
        <RoofMasterColumnManager
          layout={effectiveLayout}
          customCols={customCols}
          saving={savingLayout}
          onSave={persistColumns}
          onReset={resetColumns}
          onCreateField={createCustomField}
          onDeleteField={deleteCustomField}
          onClose={() => setShowColMgr(false)}
        />
      )}
    </div>
  );
}

/* ── Sub-components ── */

function ViewTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1.5 text-[12px] rounded-t border-b-2 whitespace-nowrap shrink-0 " +
        (active
          ? "border-bardin-orange text-bardin-ink font-semibold bg-orange-50"
          : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50")
      }
    >
      {label}
    </button>
  );
}

function YN({ v }: { v: boolean }) {
  return v
    ? <span className="inline-block w-5 h-5 leading-5 text-center text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">Y</span>
    : <span className="inline-block w-5 h-5 leading-5 text-center text-[10px] font-bold rounded-full bg-red-50 text-red-600">N</span>;
}

interface ThProps { label: string; k: string; sortKey: string; sortDir: 1 | -1; onClick: (k: string) => void }
function Th({ label, k, sortKey, sortDir, onClick }: ThProps) {
  return (
    <th
      onClick={() => onClick(k)}
      className="px-2 py-2 text-left whitespace-nowrap font-semibold cursor-pointer hover:bg-slate-100 select-none"
    >
      {label}{sortKey === k && <span className="text-bardin-orange ml-1 text-[9px]">{sortDir > 0 ? "▼" : "▲"}</span>}
    </th>
  );
}
